import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { agentApi } from "@/lib/extensions-api";
import { formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import { StatsCard } from "@/features/customer/components/StatsCard";
import { Coins, ArrowRight, ArrowLeft } from "lucide-react";

export function AgentDetailPage() {
  const { agentId } = useParams();
  const nav = useNavigate();

  const agents = useQuery({ queryKey: ["admin-agents"], queryFn: agentApi.admin.list });
  const history = useQuery({
    queryKey: ["admin-agent-history", agentId],
    queryFn: () => agentApi.admin.history(Number(agentId)),
    enabled: !!agentId,
  });

  const agent = (agents.data ?? []).find((a) => a.id === Number(agentId));

  if (agents.isLoading) return <Skeleton className="h-96" />;
  if (!agent) return null;

  return (
    <div className="space-y-6">
      <button onClick={() => nav(-1)} className="text-sm text-surface-500 hover:text-surface-800">
        ← Back
      </button>

      <header>
        <div className="text-sm text-surface-500">Agent</div>
        <h1 className="text-2xl font-bold text-surface-900">{agent.full_name}</h1>
        <div className="mt-2 flex items-center gap-3 text-sm">
          <CopyableId value={agent.user_id} />
          <StatusPill status={agent.status} />
        </div>
      </header>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatsCard icon={Coins} tone="warning" label="Available"
                    value={formatNumber(agent.available_points)}
                    hint="Ready to distribute" />
        <StatsCard icon={ArrowLeft} tone="info" label="Total Loaded"
                    value={formatNumber(agent.total_loaded)} />
        <StatsCard icon={ArrowRight} tone="success" label="Total Distributed"
                    value={formatNumber(agent.total_distributed)} />
        <StatsCard icon={Coins} tone="neutral" label="Assigned"
                    value={formatDateTime(agent.assigned_at)}
                    hint={`by ${agent.assigned_by}`} />
      </div>

      <Card>
        <CardHeader><CardTitle>Transaction History</CardTitle></CardHeader>
        <CardBody className="p-0">
          {history.isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (history.data ?? []).length === 0 ? (
            <EmptyState icon={Coins} title="No transactions yet" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Type</TH>
                  <TH>Counterparty</TH>
                  <TH className="text-right">Points</TH>
                  <TH>Reference</TH>
                  <TH>When</TH>
                </TR>
              </THead>
              <tbody>
                {history.data!.map((tx) => (
                  <TR key={tx.id}>
                    <TD>
                      {tx.transaction_type === "admin_to_agent_point_load" ? (
                        <StatusPill status="load" />
                      ) : (
                        <StatusPill status="distribute" />
                      )}
                    </TD>
                    <TD>{tx.recipient_user_id
                          ? <CopyableId value={tx.recipient_user_id} />
                          : <span className="text-xs text-surface-500">— Admin —</span>}</TD>
                    <TD className={`text-right font-mono font-semibold ${
                      tx.transaction_type === "admin_to_agent_point_load"
                        ? "text-success-700" : "text-danger-700"
                    }`}>
                      {tx.transaction_type === "admin_to_agent_point_load" ? "+" : "-"}
                      {formatNumber(tx.points)}
                    </TD>
                    <TD className="text-xs text-surface-600">{tx.reference || "—"}</TD>
                    <TD className="text-xs text-surface-500">{formatDateTime(tx.created_at)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
